import simpleRestProvider from 'ra-data-simple-rest';
import { CreateParams, CreateResult, DataProvider, fetchUtils } from 'react-admin';

import { convertFileToBase64 } from '@sh4res/bo-common/utils';

export const customDataProvider = (url: string, client = fetchUtils.fetchJson): DataProvider => {
  const baseDataProvider = simpleRestProvider(url, client);

  return {
    ...baseDataProvider,
    create: async (resource: string, params: CreateParams) => {
      if (resource === 'users/documents' && !params.data.document_name) {
        const base64file = await convertFileToBase64(
          params.data.document_file as {
            rawFile: File;
            title: string;
          },
        );

        return baseDataProvider.create(resource, {
          ...params,
          data: {
            ...params.data,
            document_file: base64file.file,
            document_name: base64file.name,
          },
        });
      }
      if (resource.endsWith('companies/payment_methods/draft_unsigned_mandate')) {
        await client(`${url}/${resource}`, {
          method: 'POST',
          body: JSON.stringify(params.data),
        });
        return {
          data: { id: `${params.data.company_id}-${params.data.acc_bic}-${params.data.acc_iban}` },
        } as CreateResult;
      }

      // Fallback to the default implementation
      return baseDataProvider.create(resource, params);
    },
    postAction: async (resource: string, data: unknown): Promise<{ data: unknown }> => {
      const { json } = await client(`${url}/${resource}`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
      return { data: json };
    },
    patchAction: async (resource: string, data: unknown): Promise<{ data: unknown }> => {
      const { json } = await client(`${url}/${resource}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
      return { data: json };
    },
    deleteAction: async (
      resource: string,
      queryParams?: URLSearchParams,
    ): Promise<{ data: unknown }> => {
      const { json } = await client(
        `${url}/${resource}${queryParams ? `?${queryParams.toString()}` : ''}`,
        { method: 'DELETE' },
      );
      return { data: json };
    },
    getOneWithQueryParams: async (
      resource: string,
      params: { id: number | string },
      queryParams: URLSearchParams,
    ) => {
      const result = await client(`${url}/${resource}/${params.id}?${queryParams.toString()}`);
      return {
        data: result.json,
      };
    },
    getListWithQueryParams: async (resource: string, queryParams: URLSearchParams) => {
      const result = await client(`${url}/${resource}?${queryParams.toString()}`);
      return {
        data: result.json,
      };
    },
    getMany: async (resource, params) => {
      const { ids } = params;
      const query = {
        filter: JSON.stringify({ id: ids }),
        range: JSON.stringify([0, Math.max(ids.length - 1, 0)]),
      };
      const { json } = await client(`${url}/${resource}?${fetchUtils.queryParameters(query)}`);
      return { data: json };
    },
    getOne: async (resource, params) => {
      const { id, meta } = params;
      const queryParams = meta?.query || {};
      const queryString = new URLSearchParams(queryParams).toString();

      const result = await client(
        `${url}/${resource}/${id}${queryString ? `?${queryString}` : ''}`,
      );
      return {
        data: result.json,
      };
    },
  };
};
